/**
 * The rail: what can be put on the board.
 *
 * It sits *on* the canvas rather than beside it, the way every board tool
 * draws its tools, because the board is the page — a 320px column of controls
 * next to it took a third of the screen away from the thing being drawn.
 *
 * Icon-first, with the name on hover and on focus and always as the accessible
 * name. A vertical rail is narrow, and a narrow rail with labels is a stack of
 * truncated words.
 *
 * The palette is identical for the therapist and for the patient. That is the
 * product requirement and it is also the point of the instrument: a genogram
 * the patient can only watch being drawn is an interview, not a shared map.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Home, Maximize2, Minimize2, Plus, StickyNote } from "lucide-react";

import {
    PERSON_GENDERS,
    SYSTEM_CATEGORIES,
    type MapKind,
    type PersonGender,
    type SystemCategory,
} from "../../domain/types";

interface Props {
    kind: MapKind;
    onAddPerson: (gender: PersonGender) => void;
    onAddSystem: (system: SystemCategory) => void;
    /** Encloses the selected people in a household. Genogram only. */
    onAddHousehold?: () => void;
    /** Pins a note to the board. On either kind: words are not notation. */
    onAddNote?: () => void;
    /** Grows or shrinks the ring the household is drawn inside. Ecomap only. */
    onResizeCentre?: (step: 1 | -1) => void;
    householdEnabled?: boolean;
    disabled?: boolean;
    /** Why the rail is off — the board is full, typically. */
    disabledReasonKey?: string;
}

export default function NodePalette({
    kind,
    onAddPerson,
    onAddSystem,
    onAddHousehold,
    onAddNote,
    onResizeCentre,
    householdEnabled = false,
    disabled = false,
    disabledReasonKey,
}: Props) {
    const { t } = useTranslation();
    const [systemsOpen, setSystemsOpen] = useState(false);
    const reason = disabled && disabledReasonKey ? t(disabledReasonKey) : undefined;

    return (
        <div
            role="toolbar"
            aria-orientation="vertical"
            aria-label={t("systemicMap.palette.add")}
            className="flex flex-col items-center gap-1 rounded-xl border border-border-subtle bg-surface-raised p-1.5 shadow-theme-md"
        >
            <>
                    {PERSON_GENDERS.map((gender) => (
                        <RailButton
                            key={gender}
                            label={t(`systemicMap.gender.${gender}`)}
                            disabled={disabled}
                            reason={reason}
                            onClick={() => onAddPerson(gender)}
                        >
                            <GenderMark gender={gender} />
                        </RailButton>
                    ))}

                    {kind === "genogram" && (
                        <>
                            <span
                                aria-hidden="true"
                                className="my-0.5 h-px w-7 bg-border-subtle"
                            />
                            <RailButton
                                label={t("systemicMap.palette.household")}
                                disabled={disabled || !householdEnabled}
                                reason={
                                    householdEnabled
                                        ? reason
                                        : t("systemicMap.rules.householdNeedsPeople")
                                }
                                onClick={() => onAddHousehold?.()}
                            >
                                <Home size={18} aria-hidden="true" />
                            </RailButton>
                        </>
                    )}
            </>

            {kind === "ecomap" && onResizeCentre && (
                <>
                    <span aria-hidden="true" className="my-0.5 h-px w-7 bg-border-subtle" />
                    <RailButton
                        label={t("systemicMap.palette.expandCentre")}
                        disabled={disabled}
                        reason={reason}
                        onClick={() => onResizeCentre(1)}
                    >
                        <Maximize2 size={18} aria-hidden="true" />
                    </RailButton>
                    <RailButton
                        label={t("systemicMap.palette.contractCentre")}
                        disabled={disabled}
                        reason={reason}
                        onClick={() => onResizeCentre(-1)}
                    >
                        <Minimize2 size={18} aria-hidden="true" />
                    </RailButton>
                </>
            )}

            {kind === "ecomap" && (
                <SystemPicker
                    open={systemsOpen}
                    disabled={disabled}
                    reason={reason}
                    onToggle={() => setSystemsOpen((current) => !current)}
                    onClose={() => setSystemsOpen(false)}
                    onPick={(system) => {
                        setSystemsOpen(false);
                        onAddSystem(system);
                    }}
                />
            )}

            {onAddNote && (
                <>
                    <span aria-hidden="true" className="my-0.5 h-px w-7 bg-border-subtle" />
                    <RailButton
                        label={t("systemicMap.palette.note")}
                        disabled={disabled}
                        reason={reason}
                        onClick={onAddNote}
                    >
                        <StickyNote size={18} aria-hidden="true" />
                    </RailButton>
                </>
            )}
        </div>
    );
}

/**
 * The eleven ecomap systems, behind one button.
 *
 * Eleven buttons down the side of the board would be a menu pretending to be a
 * toolbar; the rail stays one column wide and the choice opens beside it.
 */
function SystemPicker({
    open,
    disabled,
    reason,
    onToggle,
    onClose,
    onPick,
}: {
    open: boolean;
    disabled: boolean;
    reason?: string;
    onToggle: () => void;
    onClose: () => void;
    onPick: (system: SystemCategory) => void;
}) {
    const { t } = useTranslation();
    const wrapper = useRef<HTMLDivElement | null>(null);

    // Clicking the board should put the menu away, the same as pressing Escape.
    useEffect(() => {
        if (!open) return;
        const away = (event: MouseEvent) => {
            if (!wrapper.current?.contains(event.target as Node)) onClose();
        };
        const escape = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("mousedown", away);
        document.addEventListener("keydown", escape);
        return () => {
            document.removeEventListener("mousedown", away);
            document.removeEventListener("keydown", escape);
        };
    }, [open, onClose]);

    return (
        <div ref={wrapper} className="relative">
            <RailButton
                label={t("systemicMap.palette.addSystem")}
                disabled={disabled}
                reason={reason}
                expanded={open}
                onClick={onToggle}
            >
                <Plus size={18} aria-hidden="true" />
            </RailButton>

            {open && (
                // Centred on the rail and capped, rather than hanging from
                // the button: eleven rows starting at the button's top edge
                // run off the foot of the board.
                <div className="absolute left-full top-1/2 z-10 ml-2 max-h-[60vh] w-52 -translate-y-1/2 overflow-y-auto rounded-xl border border-border-subtle bg-surface-overlay p-1.5 shadow-theme-lg">
                    {SYSTEM_CATEGORIES.map((system) => (
                        <button
                            key={system}
                            type="button"
                            onClick={() => onPick(system)}
                            className="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 text-left text-sm text-content-primary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                            <span
                                aria-hidden="true"
                                className="h-3.5 w-3.5 shrink-0 rounded-full border-[1.5px] border-current"
                            />
                            {t(`systemicMap.system.${system}`)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/** The shape is the sex; the label says which, so it never rests on shape alone. */
function GenderMark({ gender }: { gender: PersonGender }) {
    const base = "block h-4 w-4 border-[1.75px] border-current";
    if (gender === "female") return <span aria-hidden="true" className={`${base} rounded-full`} />;
    if (gender === "unknown")
        return <span aria-hidden="true" className={`${base} rotate-45`} />;
    if (gender === "pregnancy")
        // Drawn rather than faked with borders: a CSS triangle can only be
        // solid, and a filled mark beside three outlined ones reads as a
        // different kind of thing.
        return (
            <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
                <polygon
                    points="8,1 15,15 1,15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    strokeLinejoin="round"
                />
            </svg>
        );
    return <span aria-hidden="true" className={base} />;
}

function RailButton({
    label,
    onClick,
    disabled,
    reason,
    expanded,
    children,
}: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    reason?: string;
    expanded?: boolean;
    children: React.ReactNode;
}) {
    const [showing, setShowing] = useState(false);

    return (
        <span className="relative flex">
            {showing && (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-full top-1/2 z-20 ml-2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2.5 py-1 text-xs font-medium text-white shadow-theme-md dark:bg-gray-700"
                >
                    {reason ?? label}
                </span>
            )}
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                aria-expanded={expanded}
                title={reason ?? label}
                onMouseEnter={() => setShowing(true)}
                onMouseLeave={() => setShowing(false)}
                onFocus={() => setShowing(true)}
                onBlur={() => setShowing(false)}
                className="flex h-11 w-11 items-center justify-center rounded-lg text-content-primary transition-colors duration-fast hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
                {children}
            </button>
        </span>
    );
}
